var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef, } from "react";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, useTheme, Skeleton, IconButton, } from "@mui/material";
import { Add } from "iconsax-react";
import { useGetProjectCostValuesQuery, useCreateProjectCostValueMutation, } from "../../../features/projects/projectsApi";
import { useGetCompanyMonthConfigsQuery } from "../../../features/companyMonthConfigs/companyMonthConfigsApi";
import { useSnackbar } from "notistack";
import { MaterialBottomDrawer } from "./MaterialBottomDrawer";
const formatNumber = (value, decimals = 0) => {
    if (value === "-" || value === "" || value === null || value === undefined)
        return value;
    const num = Number(value);
    if (isNaN(num))
        return value;
    const formattedNum = num.toLocaleString("nl-NL", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
    const absNum = Math.abs(num);
    const formattedAbs = absNum.toLocaleString("nl-NL", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
    if (num < 0) {
        return React.createElement("span", { style: { color: "red" } },
            "(",
            formattedAbs,
            ")");
    }
    return formattedNum;
};
const formatGuid = (guid) => {
    if (guid && guid.length === 32 && !guid.includes("-")) {
        return `${guid.slice(0, 8)}-${guid.slice(8, 12)}-${guid.slice(12, 16)}-${guid.slice(16, 20)}-${guid.slice(20)}`;
    }
    return guid;
};
const defaultRows = [
    { label: "Materiaal", values: [0, 0, 0, 0, 0, 0, 0, 0] },
    { label: "Onderaanneming", values: [0, 0, 0, 0, 0, 0, 0, 0] },
    { label: "Arbeid", values: [0, 0, 0, 0, 0, 0, 0, 0] },
];
const getBaseColumns = (m1ColumnName = "M-1", mColumnName = "M") => [
    "Forecast",
    m1ColumnName,
    "Gemaakt",
    "Besteld",
    "Nog te maken",
    mColumnName,
    "Delta M-1",
    "Voorcalculatie",
    "Delta VC",
];
const ROWS_WITH_PLUS = ["Materiaal", "Onderaanneming", "Arbeid"];
export const ForecastTableContent = forwardRef(function ForecastTableContent({ projectId, projectNumber, childProjectNumber, isChildProject, companyId, onSaved, showPlusButtons = false, isReadOnly = false, }, ref) {
    const theme = useTheme();
    const [isSaving, setIsSaving] = useState(false);
    const { data: companyMonthConfigs } = useGetCompanyMonthConfigsQuery(companyId, {
        skip: !companyId,
    });
    const activeConfigMonth = Array.isArray(companyMonthConfigs) && companyMonthConfigs.length > 0
        ? companyMonthConfigs[0].configMonth
        : "";
    const { data: costValues, isLoading: isLoadingCostValues, isFetching, isSuccess, fulfilledTimeStamp, refetch, } = useGetProjectCostValuesQuery({
        projectId: formatGuid(projectId),
        projectNumber,
        childProjectNumber,
        companyId,
        isChildProject,
    }, {
        skip: !projectId || !projectNumber || !companyId,
        refetchOnMountOrArgChange: true,
    });
    const [createCostValue] = useCreateProjectCostValueMutation();
    const { enqueueSnackbar } = useSnackbar();
    const [rows, setRows] = useState(defaultRows);
    const [rowDisableFlags, setRowDisableFlags] = useState({});
    const [remarks, setRemarks] = useState("");
    const [columns, setColumns] = useState(getBaseColumns());
    const [mColumnName, setMColumnName] = useState("M");
    const [currentConfigMonth, setCurrentConfigMonth] = useState("");
    const openedAtRef = useRef(0);
    const [materialDrawerOpen, setMaterialDrawerOpen] = useState(false);
    const [materialDrawerTitle, setMaterialDrawerTitle] = useState("");
    useEffect(() => {
        if (!projectId || !projectNumber || !companyId || materialDrawerOpen)
            return;
        openedAtRef.current = Date.now();
        setRows(defaultRows.map((r) => (Object.assign(Object.assign({}, r), { values: [...r.values] }))));
        setRemarks("");
        setColumns(getBaseColumns());
        setMColumnName("M");
        setCurrentConfigMonth("");
        refetch();
    }, [projectId, projectNumber, companyId, refetch]);
    useEffect(() => {
        var _a, _b, _c, _d, _e;
        if (!costValues)
            return;
        if (isFetching)
            return;
        if (!isSuccess)
            return;
        if (!fulfilledTimeStamp || fulfilledTimeStamp < openedAtRef.current)
            return;
        const uniqueColumnNames = Array.from(new Set(costValues
            .map((cv) => cv.columnName)
            .filter((name) => name !== "Gemaakt" && name !== "Total")));
        const columnsWithDates = uniqueColumnNames
            .map((colName) => {
            const cv = costValues.find((c) => c.columnName === colName);
            const configMonth = cv === null || cv === void 0 ? void 0 : cv.configMonth;
            const displayColumnName = cv === null || cv === void 0 ? void 0 : cv.displayColumnName;
            return {
                columnName: colName,
                displayColumnName: displayColumnName || colName,
                configMonth: configMonth ? new Date(configMonth) : null,
            };
        })
            .filter((col) => col.configMonth !== null)
            .sort((a, b) => {
            if (!a.configMonth || !b.configMonth)
                return 0;
            return b.configMonth.getTime() - a.configMonth.getTime();
        });
        const currentMColumnName = ((_a = columnsWithDates[0]) === null || _a === void 0 ? void 0 : _a.columnName) || "M";
        const m1ColumnName = ((_b = columnsWithDates[1]) === null || _b === void 0 ? void 0 : _b.columnName) || "M-1";
        const mDisplayName = ((_c = columnsWithDates[0]) === null || _c === void 0 ? void 0 : _c.displayColumnName) || "M";
        const m1DisplayName = ((_d = columnsWithDates[1]) === null || _d === void 0 ? void 0 : _d.displayColumnName) || "M-1";
        const gemaaktDisplayName = ((_e = costValues.find((cv) => cv.columnName === "Gemaakt")) === null || _e === void 0 ? void 0 : _e.displayColumnName) ||
            "Gemaakt";
        setMColumnName(currentMColumnName);
        const mConfigMonthCv = costValues.find((cv) => cv.columnName === currentMColumnName);
        setCurrentConfigMonth((mConfigMonthCv === null || mConfigMonthCv === void 0 ? void 0 : mConfigMonthCv.configMonth) || activeConfigMonth || "");
        const dynamicColumns = [
            "Forecast",
            m1DisplayName,
            gemaaktDisplayName,
            "Besteld",
            "Nog te maken",
            mDisplayName,
            "Delta M-1",
            "Voorcalculatie",
            "Delta VC",
        ];
        setColumns(dynamicColumns);
        const newRows = defaultRows.map((r) => (Object.assign(Object.assign({}, r), { values: [...r.values] })));
        // Track disable flags for each row (CostComponent-driven overrides)
        const disableFlags = {};
        ["Materiaal", "Onderaanneming", "Arbeid"].forEach((rowLabel) => {
            const cv = costValues.find((c) => c.rowName === rowLabel && c.columnName === currentMColumnName);
            if (rowLabel === "Materiaal")
                disableFlags[rowLabel] = !!(cv === null || cv === void 0 ? void 0 : cv.disableMeterial);
            if (rowLabel === "Onderaanneming")
                disableFlags[rowLabel] = !!(cv === null || cv === void 0 ? void 0 : cv.disableOnderaanneming);
            if (rowLabel === "Arbeid")
                disableFlags[rowLabel] = !!(cv === null || cv === void 0 ? void 0 : cv.disableArbeid);
        });
        setRowDisableFlags(disableFlags);
        const colMap = {
            [m1ColumnName]: 0,
            Gemaakt: 1,
            Besteld: 2,
            "Nog te maken": 3,
            [currentMColumnName]: 4,
            "Delta M-1": 5,
            Voorcalculatie: 6,
            "Delta VC": 7,
        };
        costValues.forEach((cv) => {
            var _a;
            const row = newRows.find((r) => r.label === cv.rowName);
            if (row) {
                const colIdx = colMap[cv.columnName];
                if (colIdx !== undefined) {
                    row.values[colIdx] = (_a = cv.value) !== null && _a !== void 0 ? _a : 0;
                }
            }
        });
        newRows.forEach((row) => {
            const m1Value = Number(row.values[0]) || 0;
            const mValue = Number(row.values[4]) || 0;
            const gemaaktValue = Number(row.values[1]) || 0;
            row.values[5] = mValue - m1Value;
            row.values[3] = mValue ? mValue - gemaaktValue : m1Value - gemaaktValue;
            row.values[7] = "-";
        });
        // Removed Uren and Tarief row logic
        const remarkEntry = costValues.find((cv) => cv.opmerkingen);
        if (remarkEntry) {
            setRemarks(remarkEntry.opmerkingen || "");
        }
        setRows(newRows);
    }, [
        costValues,
        isFetching,
        isSuccess,
        fulfilledTimeStamp,
        activeConfigMonth,
    ]);
    const handleCellChange = (rowIdx, colIdx, value) => {
        if (!/^-?\d*\.?\d*$/.test(value))
            return;
        const updatedRows = rows.map((row, idx) => {
            if (idx === rowIdx) {
                const newValues = row.values.map((v, i) => (i === colIdx ? value : v));
                if (colIdx === 4) {
                    const m1Value = Number(newValues[0]) || 0;
                    const mValue = Number(value) || 0;
                    const gemaaktValue = Number(newValues[1]) || 0;
                    newValues[5] = mValue - m1Value;
                    newValues[3] = mValue
                        ? mValue - gemaaktValue
                        : m1Value - gemaaktValue;
                    newValues[7] = "-";
                }
                return Object.assign(Object.assign({}, row), { values: newValues });
            }
            return row;
        });
        // Removed Uren and Tarief row logic
        setRows(updatedRows);
    };
    const getTotalForColumn = (colIdx) => {
        var _a;
        if (colIdx === 0)
            return "Totaal";
        const columnName = columns[colIdx];
        const valuesIndex = colIdx - 1;
        if (columnName === "Gemaakt" && costValues) {
            const totalGemaakt = costValues.find((cv) => cv.rowName === "Total" && cv.columnName === "Gemaakt");
            if (totalGemaakt !== undefined && totalGemaakt.value !== null) {
                return totalGemaakt.value;
            }
        }
        if (columnName === "Delta VC") {
            const rowsToSum = ["Materiaal", "Onderaanneming", "Arbeid"];
            let totalM = 0;
            let totalVc = 0;
            rows.forEach((row) => {
                if (rowsToSum.includes(row.label)) {
                    const mVal = row.values[4];
                    const vcVal = row.values[6];
                    if (!isNaN(Number(mVal)))
                        totalM += Number(mVal);
                    if (!isNaN(Number(vcVal)))
                        totalVc += Number(vcVal);
                }
            });
            return totalM - totalVc;
        }
        if (columnName === "Nog te maken" && costValues) {
            const rowsToSum = ["Materiaal", "Onderaanneming", "Arbeid"];
            let totalM = 0;
            let totalM1 = 0;
            rows.forEach((row) => {
                if (rowsToSum.includes(row.label)) {
                    const mVal = row.values[4];
                    const m1Val = row.values[0];
                    if (!isNaN(Number(mVal)))
                        totalM += Number(mVal);
                    if (!isNaN(Number(m1Val)))
                        totalM1 += Number(m1Val);
                }
            });
            const totalGemaakt = costValues.find((cv) => cv.rowName === "Total" && cv.columnName === "Gemaakt");
            const gemaaktTotal = (_a = totalGemaakt === null || totalGemaakt === void 0 ? void 0 : totalGemaakt.value) !== null && _a !== void 0 ? _a : 0;
            return totalM ? totalM - gemaaktTotal : totalM1 - gemaaktTotal;
        }
        const rowsToSum = ["Materiaal", "Onderaanneming", "Arbeid"];
        let sum = 0;
        rows.forEach((row) => {
            if (rowsToSum.includes(row.label)) {
                const val = row.values[valuesIndex];
                if (!isNaN(Number(val)))
                    sum += Number(val);
            }
        });
        return sum === 0 ? 0 : sum;
    };
    const save = () => __awaiter(this, void 0, void 0, function* () {
        setIsSaving(true);
        try {
            const payloads = [];
            rows.forEach((row) => {
                const mValue = row.values[4];
                const numericValue = typeof mValue === "string" && mValue !== "-"
                    ? parseFloat(mValue)
                    : typeof mValue === "number"
                        ? mValue
                        : 0;
                const finalValue = isNaN(numericValue) ? 0 : numericValue;
                if (row.label !== "Tarief") {
                    payloads.push({
                        projectId: formatGuid(projectId),
                        projectNumber,
                        childProjectNumber,
                        rowName: row.label,
                        columnName: mColumnName,
                        value: finalValue,
                        opmerkingen: remarks,
                        companyId: companyId,
                    });
                }
            });
            if (payloads.length > 0) {
                yield createCostValue(payloads).unwrap();
            }
            onSaved === null || onSaved === void 0 ? void 0 : onSaved();
        }
        catch (_a) {
            enqueueSnackbar("Er is een fout opgetreden bij het opslaan", {
                variant: "error",
            });
        }
        finally {
            setIsSaving(false);
        }
    });
    useImperativeHandle(ref, () => ({
        save,
        isSaving,
    }), [
        isSaving,
        rows,
        remarks,
        mColumnName,
        projectId,
        projectNumber,
        childProjectNumber,
        companyId,
        createCostValue,
        enqueueSnackbar,
        onSaved,
    ]);
    return (React.createElement(Box, null,
        isLoadingCostValues ? (React.createElement(React.Fragment, null,
            React.createElement(TableContainer, { sx: {
                    borderRadius: 2,
                    mb: 2,
                    width: "100%",
                    overflowX: "auto",
                } },
                React.createElement(Table, { size: "small", sx: { width: "100%" } },
                    React.createElement("colgroup", null, columns.map((col, idx) => (React.createElement("col", { key: col, style: { minWidth: idx === 0 ? 140 : 120 } })))),
                    React.createElement(TableHead, null,
                        React.createElement(TableRow, null, columns.map((col, idx) => (React.createElement(TableCell, { key: col, align: idx === 0 ? "left" : "right", sx: {
                                fontWeight: 600,
                                minWidth: 140,
                                background: "#F4F6F8",
                                borderBottom: "none",
                                color: theme.palette.tertiary[30],
                            } }, col))))),
                    React.createElement(TableBody, null,
                        [...Array(5)].map((_, rowIdx) => (React.createElement(TableRow, { key: rowIdx },
                            React.createElement(TableCell, { align: "left", sx: { minWidth: 140 } },
                                React.createElement(Skeleton, { variant: "text", width: 100 })),
                            columns.slice(1).map((_, colIdx) => (React.createElement(TableCell, { align: "right", key: colIdx, sx: { minWidth: 140 } },
                                React.createElement(Skeleton, { variant: "text", width: 60, sx: { mx: "auto" } }))))))),
                        React.createElement(TableRow, { sx: { background: "#3192D014" } },
                            React.createElement(TableCell, { align: "left", sx: { minWidth: 140 } },
                                React.createElement(Skeleton, { variant: "text", width: 60 })),
                            columns.slice(1).map((_, colIdx) => (React.createElement(TableCell, { key: colIdx, align: "right", sx: { minWidth: 140 } },
                                React.createElement(Skeleton, { variant: "text", width: 60, sx: { mx: "auto" } })))))))),
            React.createElement(Skeleton, { variant: "rounded", width: "100%", height: 100, sx: { borderRadius: 2, mb: 2 } }))) : (React.createElement(React.Fragment, null,
            React.createElement(TableContainer, { sx: {
                    borderRadius: 2,
                    mb: 2,
                    width: "100%",
                    overflowX: "auto",
                } },
                React.createElement(Table, { size: "small", sx: { width: "100%" } },
                    React.createElement("colgroup", null, columns.map((col, idx) => (React.createElement("col", { key: col, style: { minWidth: idx === 0 ? 140 : 120 } })))),
                    React.createElement(TableHead, null,
                        React.createElement(TableRow, null, columns.map((col, idx) => (React.createElement(TableCell, { key: col, align: idx === 0 ? "left" : "right", sx: {
                                fontWeight: 600,
                                minWidth: 140,
                                background: "#F4F6F8",
                                borderBottom: "none",
                                color: theme.palette.tertiary[30],
                            } }, col))))),
                    React.createElement(TableBody, null,
                        isLoadingCostValues || isFetching ? (React.createElement(React.Fragment, null,
                            [...Array(5)].map((_, rowIdx) => (React.createElement(TableRow, { key: rowIdx },
                                React.createElement(TableCell, { align: "left", sx: { minWidth: 140 } },
                                    React.createElement(Skeleton, { variant: "text", width: 100 })),
                                columns.slice(1).map((_, colIdx) => (React.createElement(TableCell, { align: "right", key: colIdx, sx: { minWidth: 140 } },
                                    React.createElement(Skeleton, { variant: "text", width: 60, sx: { mx: "auto" } }))))))),
                            React.createElement(TableRow, { sx: { background: "#F4F6F8" } },
                                React.createElement(TableCell, { align: "left", sx: { minWidth: 140 } },
                                    React.createElement(Skeleton, { variant: "text", width: 60 })),
                                columns.slice(1).map((_, colIdx) => (React.createElement(TableCell, { key: colIdx, align: "right", sx: { minWidth: 160 } },
                                    React.createElement(Skeleton, { variant: "text", width: 60, sx: { mx: "auto" } }))))))) : (rows.map((row, rowIdx) => (React.createElement(TableRow, { key: row.label },
                            React.createElement(TableCell, { align: "left", sx: { minWidth: 160 } },
                                React.createElement(Box, { sx: {
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                    } },
                                    showPlusButtons &&
                                        ROWS_WITH_PLUS.includes(row.label) && (React.createElement(IconButton, { size: "small", sx: {
                                            width: 28,
                                            height: 28,
                                            backgroundColor: "#3192D00D",
                                            "&:hover": {
                                                backgroundColor: theme.palette.grey[300],
                                            },
                                        }, "aria-label": "Add", onClick: () => {
                                            setMaterialDrawerTitle(row.label);
                                            setMaterialDrawerOpen(true);
                                        } },
                                        React.createElement(Add, { size: 16, color: "#2476A9" }))),
                                    row.label)),
                            row.values.map((val, colIdx) => (React.createElement(TableCell, { align: "right", key: colIdx, sx: { minWidth: 140 } }, colIdx === 4 && row.label !== "Tarief" ? (React.createElement(TextField, { value: val, onChange: (e) => handleCellChange(rowIdx, colIdx, e.target.value), size: "small", variant: "outlined", disabled: !!rowDisableFlags[row.label] || isReadOnly, sx: {
                                    width: 70,
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "24px",
                                    },
                                    "& .MuiInputBase-input": {
                                        textAlign: "right",
                                        p: 0.5,
                                        pr: 1,
                                    },
                                } })) : (formatNumber(val, row.label === "Tarief" ? 0 : undefined))))))))),
                        isLoadingCostValues || isFetching ? (React.createElement(TableRow, { sx: {
                                background: "#F4F6F8",
                                "& .MuiTableCell-root": {
                                    fontWeight: 600,
                                    color: theme.palette.tertiary[30],
                                    borderTop: `2px solid ${theme.palette.divider}`,
                                },
                            } }, columns.map((col, colIdx) => (React.createElement(TableCell, { key: col, align: colIdx === 0 ? "left" : "right", sx: { minWidth: 140 } },
                            React.createElement(Skeleton, { variant: "text", width: colIdx === 0 ? 60 : 90, height: 22 })))))) : (React.createElement(TableRow, { sx: {
                                background: "#F4F6F8",
                                "& .MuiTableCell-root": {
                                    fontWeight: 600,
                                    color: theme.palette.tertiary[30],
                                    borderTop: `2px solid ${theme.palette.divider}`,
                                },
                            } }, columns.map((col, colIdx) => (React.createElement(TableCell, { key: col, align: colIdx === 0 ? "left" : "right", sx: { minWidth: 140 } }, colIdx === 0
                            ? "Totaal"
                            : formatNumber(getTotalForColumn(colIdx)))))))))),
            React.createElement(TextField, { fullWidth: true, multiline: true, minRows: 3, placeholder: "Opmerkingen", value: remarks, onChange: (e) => setRemarks(e.target.value), sx: {
                    background: "#fff",
                    borderRadius: 2,
                    "& .MuiOutlinedInput-root": {
                        background: "#fff",
                    },
                } }))),
        React.createElement(MaterialBottomDrawer, { open: materialDrawerOpen, onClose: () => {
                setMaterialDrawerOpen(false);
            }, title: materialDrawerTitle, companyId: companyId, externalId: materialDrawerTitle, configMonth: currentConfigMonth || activeConfigMonth, companyConfigMonth: activeConfigMonth, projectNumber: projectNumber, childProjectNumber: childProjectNumber })));
});
